import { SPACING } from '@alto/design-library-tokens';
import { ActionSheetContext, Card, LgPadding, ListBase, useScreenSize } from '@alto/design-system';
import { Experimentation } from '@alto/experimentation';
import { PrescriptionStatusTracker, getDeliveryEstimatedPrice, getPriceCopy } from '@alto/features';
import { type Prescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/prescription';
import { type SourcePrescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/source_prescription';
import { PrescriptionStatusMap } from '@alto/scriptdash/alto/patient_app/types/v1/prescription_status';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { getNextDeliveriesMappedToPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import { SECTION_LABELS } from '~shared/features/homescreen/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useSelectorShared } from '~shared/store';
import { type Mutable, PrescriptionInfo, type Props as PrescriptionInfoProps } from './PrescriptionInfo';
import { MedicationCTA } from '~web/features/homescreen/sections/MedicationCTA';

const CustomCard = styled(Card)<{ isSMScreenOrBigger?: boolean }>`
  margin: 0 0 ${SPACING.STATIC.XL.px};
  ${({ isSMScreenOrBigger }) => isSMScreenOrBigger && ` margin: 0 0 ${SPACING.STATIC.LG.value * 2}px;`}
  z-index: 0;
`;

type Props = {
  readonly prescriptions: (SourcePrescription | Prescription)[];
};

export default function NewPrescriptions({ prescriptions }: Props) {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const deliveriesByPrescriptionID = useSelectorShared(getNextDeliveriesMappedToPrescriptionID);
  const { isSMScreenOrBigger } = useScreenSize();
  const { value: shouldViewPriceInCartOnly } = Experimentation.useFeatureFlag('view_price_in_cart_only');

  const Rows = prescriptions.map((prescription) => {
    const { name, status } = prescription;

    const prescriptionInfoProps = {
      title: name,
      origin: ORIGIN_NAMES.HOMESCREEN,
    } as Mutable<PrescriptionInfoProps>;

    let prescriptionID: number | undefined = undefined;
    // check if the rx is not a source prescription
    if ('id' in prescription) {
      prescriptionID = prescription.id;
      const { id, image_url, family_member_name, manual_coupon_eligible, price } = prescription;
      const delivery = deliveriesByPrescriptionID[id];
      const estimatedPrice = getDeliveryEstimatedPrice(delivery);

      prescriptionInfoProps.manualCouponEligible = manual_coupon_eligible ?? false;
      prescriptionInfoProps.byline = family_member_name || undefined;
      prescriptionInfoProps.prescriptionID = id;
      prescriptionInfoProps.imageURL = image_url || undefined;

      const formattedPrice = getPriceCopy({
        estimatedPrice,
        isPrescriptionReady: status === 'ready',
        shouldShowPrice: !shouldViewPriceInCartOnly,
        price,
      });
      prescriptionInfoProps.description = formattedPrice;
    }

    prescriptionInfoProps.CTA = (
      <div
        role="button"
        tabIndex={0}
        onClick={(event) => {
          // Prevent clicks on CTA actions from propagating to parent
          event.stopPropagation();
        }}
        onKeyUp={(event) => {
          // Prevent clicks on CTA actions from propagating to parent
          event.stopPropagation();
        }}
      >
        <MedicationCTA
          prescription={prescription}
          openPrescriptionTrackerModal={() => {
            // prescription.status PROGYNY_EARLY_ORDER_ELIGIBLE is a special case
            // the add to cart button that leads to a native only progyny flow
            // but the button is disabled on web until progyny ordering is available on web
            setActiveActionSheet(
              <PrescriptionStatusTracker
                prescriptionId={prescriptionID}
                origin="NewPrescriptions"
                prescriptionStatus={prescription.status}
                prescriptionName={prescription.name}
              />,
            );
          }}
        />
      </div>
    );

    prescriptionInfoProps.isCTAButton =
      PrescriptionStatusMap.MULTIPLE_PRICES === status ||
      PrescriptionStatusMap.READY === status ||
      PrescriptionStatusMap.PROGYNY_EARLY_ORDER_ELIGIBLE === status;

    return {
      key: prescription.name,
      component: <PrescriptionInfo {...prescriptionInfoProps} />,
    };
  });

  return (
    <CustomCard
      title={SECTION_LABELS.NEW_PRESCRIPTIONS}
      illustrationSrc={getImage('pillBottle')}
      illustrationAccessibilityLabel={IMG_ALT_TEXTS.pillBottle}
      isSMScreenOrBigger={isSMScreenOrBigger}
    >
      <LgPadding topPadding={SPACING.STATIC.NONE}>
        <section>
          <ListBase items={Rows} />
        </section>
      </LgPadding>
    </CustomCard>
  );
}
